import { Card, Container, IconButton, Link, Stack, Typography } from '@mui/material';
import CarouselCenterMode from 'src/components/carousel/sections/carousel-center-mode';
import AppButton from 'src/components/common/AppButton';
import AppLoadingAndErrorWrapper from 'src/components/common/AppLoadingAndErrorWrapper';
import NewAppButton from 'src/components/common/NewAppButton';
import { ConfirmDialog } from 'src/components/custom-dialog';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFRadioGroup from 'src/components/hook-form/rhf-radio-group';
import Iconify from 'src/components/iconify';
import { RADIO_OPTIONS } from 'src/constants/teacher-requests-status-options';
import { AuthorizedUsersQuery } from 'src/graphql';
import { useRouter } from 'src/routes/hooks';
import useSchoolRequestDetails from './use-school-request-details';

const SchoolRequestDetails = () => {
  const {
    SHOW_DATA,
    confirm,
    handleChangeStatus,
    isApproving,
    isLoading,
    isRejecting,
    methods,
    onSubmit,
    t,
    schoolData,
  } = useSchoolRequestDetails();

  return (
    <Container>
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <AppLoadingAndErrorWrapper isLoading={isLoading}>
          <Stack spacing={5}>
            {/* Request info */}
            <Stack spacing={1}>
              <Typography variant="h5">{t('schoolRequestDetails.requestInfo.title')}</Typography>
              <Card sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {SHOW_DATA.map((obj) => (
                    <Stack alignItems="center" spacing={1} direction={{ md: 'row', xs: 'column' }}>
                      <Typography variant="body1" fontWeight={500}>
                        {obj.label}:
                      </Typography>
                      <Typography variant="body2">{obj.value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Card>
            </Stack>

            {/* Files */}
            {!!schoolData?.files.length && (
              <Stack spacing={1}>
                <Typography variant="h5">{t('schoolRequestDetails.files.title')}</Typography>
                {schoolData?.files.length! > 1 ? (
                  <CarouselCenterMode
                    data={schoolData?.files!}
                    CarouselItem={(props) => <CarouselItem {...props} />}
                  />
                ) : (
                  <Stack alignItems="center" justifyContent="center">
                    <CarouselItem {...schoolData?.files[0]!} />
                  </Stack>
                )}
              </Stack>
            )}

            {/* Files */}
            <Stack spacing={1}>
              <Typography variant="h5">
                {t('schoolRequestDetails.changeRequestStatus.title')}
              </Typography>
              <RHFRadioGroup
                name="status"
                options={RADIO_OPTIONS(t)}
                sx={{ display: 'flex', flexDirection: 'row' }}
              />
            </Stack>

            {/* Buttons */}
            <NewAppButton
              label={t('schoolRequestDetails.changeRequestStatus.title')}
              size="small"
              color={methods.watch().status === 'approved' ? 'primary' : 'error'}
              disabled={methods.watch().status === 'pending'}
              type="submit"
            />
          </Stack>
        </AppLoadingAndErrorWrapper>
      </FormProvider>

      {/* Confirm rejection */}
      <ConfirmDialog
        open={confirm.value}
        onClose={(event: React.SyntheticEvent<Element, Event>, reason: string) => {
          if (reason && reason === 'backdropClick') {
            confirm.onFalse();
          }
        }}
        content={t(
          `schoolRequestDetails.${
            methods.watch().status === 'approved' ? 'approve' : 'reject'
          }.areYouSure`
        )}
        action={
          <>
            <AppButton
              fullWidth
              label={t('publicWords.no')}
              variant="outlined"
              onClick={() => {
                confirm.onFalse();
              }}
            />
            <AppButton
              fullWidth
              label={t('publicWords.yes')}
              isLoading={isRejecting || isApproving}
              onClick={handleChangeStatus}
            />
          </>
        }
      />
    </Container>
  );
};

export default SchoolRequestDetails;

const CarouselItem = (props: AuthorizedUsersQuery['authorized_users'][0]['files'][0]) => {
  const router = useRouter();
  return (
    <Card sx={{ p: 1 }}>
      <Stack alignItems="center" spacing={1}>
        <img
          src={props.url!}
          width={400}
          height={200}
          style={{ borderRadius: 8, objectFit: 'contain' }}
        />
        <Link href={props.url!} target="_blank">
          <IconButton color="info">
            <Iconify icon="system-uicons:external" />
          </IconButton>
        </Link>
      </Stack>
    </Card>
  );
};
